import {
  AppCard,
  Button,
  Icon,
  PageContainer,
} from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiPencil } from "@mdi/js";
import { Box, CircularProgress, Typography } from "@mui/material";
import { createFileRoute } from "@tanstack/react-router";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useShowBillOfMaterialsMgmtRequest } from "../../../../../__generated__/orval/endpoints/bill-of-materials-mgmt-requests/bill-of-materials-mgmt-requests";
import { MmHistoryLogs } from "../../../../../components/molecules/MmHistoryLogs";
import { MmRequestAttachments } from "../../../../../components/molecules/MmRequestAttachments";
import { MmRequestHeader } from "../../../../../components/molecules/MmRequestHeader";
import { MmRequestComments } from "../../../../../components/molecules/MmRequestsComments";
import {
  MmRequestTabs,
  type MmRequestTabValue,
} from "../../../../../components/molecules/MmRequestTabs";
import { useAuth } from "../../../../../hooks/useAuth";
import {
  type MmRequestTabsSearch,
  mmRequestTabsSearchSchema,
} from "../../mm_requests/-schemas/mmRequestTabsSearchSchema";
import { BillOfMaterialsMgmtRequestDetails } from "../-internal-components/BillOfMaterialsMgmtRequestDetails";

const ShowBillOfMaterialsMgmtRequest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const { user } = useAuth();
  const { billOfMaterialsMgmtRequestId } = Route.useParams();
  const { tab } = Route.useSearch();
  const id = Number(billOfMaterialsMgmtRequestId);

  const { data, isLoading, isError } = useShowBillOfMaterialsMgmtRequest(id);

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(t("mmRequests.fetchError"), { variant: "error" });
    }
  }, [isError, t]);

  const handleTabChange = useCallback(
    (newValue: MmRequestTabValue) => {
      navigate({
        replace: true,
        resetScroll: false,
        search: (prev) => ({
          ...prev,
          tab: newValue as MmRequestTabsSearch["tab"],
        }),
      });
    },
    [navigate],
  );

  const handleEdit = useCallback(() => {
    navigate({
      params: { billOfMaterialsMgmtRequestId },
      to: "/vbuyer/bill_of_materials_mgmt_requests/$billOfMaterialsMgmtRequestId/edit",
    });
  }, [billOfMaterialsMgmtRequestId, navigate]);

  const mmRequest = data?.mm_request;
  const mmRequestId = mmRequest?.id;
  const isCompleted = mmRequest?.process_instance?.completed ?? false;
  const userCanEdit = useMemo(() => {
    if (!user || !mmRequest) return false;

    return (
      (user.roles_names.includes("Edit material requests") ||
        user.id === mmRequest.assigned_to?.id) &&
      !mmRequest.process_instance?.completed
    );
  }, [mmRequest, user]);

  const title = mmRequestId
    ? `${t("common.billOfMaterialsMgmtRequest")} #${mmRequestId}`
    : t("common.billOfMaterialsMgmtRequest");

  const keysToInvalidate: Array<string | number> = [];
  keysToInvalidate.push(`/api/v3/bill_of_materials_mgmt_requests/${id}`);

  if (isLoading) {
    return (
      <PageContainer showBackButton title={title}>
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer showBackButton title={title}>
      {!!mmRequest && (
        <MmRequestHeader
          id={mmRequest.id}
          keysToInvalidate={keysToInvalidate}
        />
      )}

      <AppCard>
        <MmRequestTabs onChange={handleTabChange} value={tab} />
        <Box sx={{ marginTop: "32px" }}>
          {tab === "details" && mmRequest && (
            <AppCard
              header={
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography variant="h6">{t("details")}</Typography>
                  {userCanEdit && (
                    <Button
                      onClick={handleEdit}
                      startIcon={<Icon icon={mdiPencil} />}
                      variant="outlined"
                    >
                      {t("common.edit")}
                    </Button>
                  )}
                </Box>
              }
            >
              <BillOfMaterialsMgmtRequestDetails
                mmRequest={mmRequest}
                onEdit={handleEdit}
                showCreatedUpdated={false}
                showEditButton={false}
                showPreviousTicket
                showRequesterArea={false}
              />
            </AppCard>
          )}
          {tab === "attachments" && mmRequestId && (
            <MmRequestAttachments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "comments" && mmRequestId && (
            <MmRequestComments
              isCompleted={isCompleted}
              mmRequestId={mmRequestId}
            />
          )}
          {tab === "history" && mmRequestId && (
            <MmHistoryLogs mmRequestId={mmRequestId} />
          )}
        </Box>
      </AppCard>
    </PageContainer>
  );
};

export const Route = createFileRoute(
  "/_private/vbuyer/bill_of_materials_mgmt_requests/$billOfMaterialsMgmtRequestId/",
)({
  component: ShowBillOfMaterialsMgmtRequest,
  validateSearch: mmRequestTabsSearchSchema,
});
